import * as d3 from 'd3';
import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
    setSelectedVendorTracePrevalenceFilters,
} from '../../../actions/diagnose_vendor_trace_actions';
import VendorTraceMetricLegend from './VendorTraceMetricLegend';
import VendorTraceNonDisclosedFilter from './VendorTraceNonDisclosedFilter';
import VendorTracePrevalenceFilter from './VendorTracePrevalenceFilter';
import VendorTracePriorToConsentFilter from './VendorTracePriorToConsentFilter';
import VendorTraceZoom from './VendorTraceZoom';
import RuleService from './link_color_rules/rule_service';

function Tree({
    onClickShowMoreLayersButton,
    onClickZoomButton,
    selectedMetric,
    selectedVendorName,
    filteredData,
    selectedVendorTracePrevalenceFilter,
    toggleChildren,
    searchByVendorNameMode,
    consentTypeName,
    priorToConsentFilterList
}) {
    const ref = useRef();
    const [disclosedFilter, setDisclosedFilter] = useState(['disclosed', 'disclosed-triggering-non-disclosed', 'non-disclosed']);
    const [chartHeight, setChartHeight] = useState(1000);
    const [chartWidth, setChartWidth] = useState(600);
    const [scaleValue, setScaleValue] = useState(0.6);
    const [zoomSide, setZoomSide] = useState('next');
    const refreshChart = () => {
        d3.selectAll('.chart #vendor-trace-svg > *').remove();
        !_.isEmpty(filteredData) && draw();
    };

    useEffect(() => {
        !_.isEmpty(filteredData) && draw();
    }, [filteredData]);

    useEffect(() => {
        refreshChart();
    }, [filteredData, selectedMetric, selectedVendorName, disclosedFilter, priorToConsentFilterList]);

    useEffect(() => {
        onClickZoomButton(d3.select('#vendorTraceTreeWrapper').node().getBoundingClientRect().width > chartWidth - 150);
    }, [chartWidth]);

    useEffect(() => {
        onClickShowMoreLayersButton(zoomSide);
    }, [zoomSide]);

    const onToggleChildren = (id) => {
        toggleChildren(id);
    };

    const isCollapsedNode = (d) => d.data.attributes.hasChildren && !d.data.children.length;
    const draw = () => {
        const margin = { top: 20, left: 26 };
            const labelWidth = 80;
            const labelHeight = 34;
            const width = '800';
            const height = '450';

        const treemap = d3.tree().size([height, width]).nodeSize([40, 235]);

        let nodes = d3.hierarchy(filteredData, (d) => d.children);

        nodes = treemap(nodes);

        const nodeMap = {};
        nodes.descendants().forEach((d) => {
            if (nodeMap[d.depth] === undefined || d.x < nodeMap[d.depth]) {
                nodeMap[d.depth] = d.x;
            }
            d.y = d.depth * 230;
            d.x -= nodeMap[d.depth];
        });

        const svg = d3.select(ref.current);
            const g = svg.append('g')
                .attr('transform',
                    `translate(${margin.left + labelWidth / 2},${margin.top + labelHeight})`,
                ).append('g')
                .attr('transform',
                    `${'translate(0,0)' +
                    ' scale('}${scaleValue})`,
                )
        ;

        function pluck(array, key) {
            return array.map((o) => o[key]);
        }

        const depthHash = _.uniq(pluck(nodes.descendants(), 'depth')).sort();
        const levelSVG = d3.select(ref.current).select('g').append('g')
            .attr('transform',
                `translate(-${margin.left},` + `-${labelHeight})` +
                ` scale(${scaleValue})`,
            )
            .attr('class', 'levels-svg');

        const levels = levelSVG.selectAll('g.level');
        levels.data(depthHash)
            .enter().append('g')
            .attr('class', 'level')
            .attr('transform', (d) => {
                return `translate(${(d * 230) - (margin.left / 2)},0)`;
            })
            .append('text')
            .text((d) => {
                return `Layer ${Number(d) + 1}`;
            });

        const rulesService = new RuleService();
        const filtersSet = (d) => ({
            prevalence_filter: selectedVendorTracePrevalenceFilter,
            disclosed_filter: disclosedFilter,
            vendor_name: selectedVendorName,
            metric_name: selectedMetric,
            is_collapsed_children: isCollapsedNode(d),
            search_by_vendor_name_mode: searchByVendorNameMode,
            prior_to_consent_filter: priorToConsentFilterList,
        });

        const setColor = (d) => {
            return rulesService.getColor(filtersSet(d), d);
        };
        const setTextColor = (d) => {
            return rulesService.getTextColor(filtersSet(d), d);
        };
        const setNodeBackgroundColor = (d) => {
            return rulesService.getNodeBackgroundColor(filtersSet(d), d);
        };

        const link = g.selectAll('.link')
            .data(nodes.descendants().slice(1))
            .enter().append('path')
            .attr('class', 'vendor-trace-custom-link')
            .style('stroke', (d) => {
                return setColor(d);
            })
            .attr('d', (d) => {
                return `M${d.y - labelWidth / 2},${d.x
                     }C${(d.y + d.parent.y) / 2},${d.x
                     } ${(d.y + d.parent.y) / 2 - labelWidth / 2},${d.parent.x
                     } ${d.parent.y + labelWidth / 2},${d.parent.x}`;
            });

        const node = g.selectAll('.node')
            .data(nodes.descendants())
            .enter()
            .append('g')
            .attr('class', (d) => `node${d.children ? ' node--internal' : ' node--leaf'}`)
            .attr('transform', (d) => `translate(${d.y},${d.x})`)
            .on('click', (event, d) => {
                if (!selectedVendorName) {
                    onToggleChildren(d.data.attributes.map_key);
                }
            });

        node.append('foreignObject')
            .attr('x', -labelWidth / 2)
            .attr('y', -labelHeight / 2)
            .attr('width', labelWidth)
            .attr('height', labelHeight)
            .attr('class', (d) => {
                return `vendor-trace-node-item-container ${d.data.attributes.hasChildren && !selectedVendorName ? (d.data.children.length ? 'node-children-visible' : 'node-children-hidden') : ''}`;
            })
            .style('border-color', (d) => {
                return setColor(d);
            })
            .style('background-color', (d) => {
                return setNodeBackgroundColor(d);
            })
            .style('color', (d) => {
                return setTextColor(d);
            })
            .append((d) => {
                const label = document.createElement('div');
                label.setAttribute('class', 'vendor-trace-node-item');
                label.append(d.data.name);
                return label;
            });

        node.append('foreignObject')
            .attr('x', '42')
            .attr('y', '-16')
            .attr('class', 'tooltip-container')
            .append((d) => {
                const tooltip = document.createElement('div');
                tooltip.setAttribute('class', 'tooltip');
                const h5 = document.createElement('h5');
                h5.append(d.data.name);
                const pDomain = document.createElement('p');
                pDomain.append(d.data.attributes.vendor_domain);
                const p = document.createElement('p');
                p.append(`Prevalence: ${Math.round(d.data.attributes.relative_prevalence * 100)}%`);
                tooltip.append(h5);
                tooltip.append(pDomain);
                tooltip.append(p);
                return tooltip;
            });

        g.selectAll('.node > .vendor-trace-node-item-container')
            .on('mouseover', function () {
                this.parentNode.getElementsByClassName('tooltip-container').item(0).setAttribute('style', 'opacity: 1');
            })
            .on('mouseleave', function () {
                this.parentNode.getElementsByClassName('tooltip-container').item(0).setAttribute('style', 'opacity: 0');
            });

        setChartHeight(d3.select('#vendor-trace-svg > g').node().getBoundingClientRect().height + 100);
        setChartWidth(d3.select('#vendor-trace-svg > g').node().getBoundingClientRect().width + 180);

        function zoomed({ transform }) {
            g.attr('transform', transform);
            levelSVG.attr('transform', `translate(-${margin.left},` + `-${labelHeight})` +
                ` scale(${transform.k})`);
        }

        const zoom = d3.zoom()
            .on('zoom', zoomed)
            .on('end', (e) => {
                setChartWidth(d3.select('#vendor-trace-svg > g').node().getBoundingClientRect().width + 180);
                setChartHeight(d3.select('#vendor-trace-svg > g').node().getBoundingClientRect().height + 100);
            });

        d3.select('#zoom_in').on('click', () => {
            setScaleValue(1);
            zoom.scaleTo(svg.transition().duration(750), 1, [0, 0]);
            onClickZoomButton(false);
        });
        d3.select('#zoom_out').on('click', () => {
            const scale = d3.select('#vendorTraceTreeWrapper').node().getBoundingClientRect().width / (d3.select('#vendor-trace-svg').node().getBoundingClientRect().width + 150);
            setScaleValue(0.6);
            zoom.scaleBy(svg.transition().duration(750),
                0.6,
                [0, 0]);
            onClickZoomButton(true);
            setZoomSide('next');
            document.getElementById('vendorTraceData').scrollIntoView({ behavior: 'smooth', block: 'start' });
        });

        d3.select('#prev-layers-button').on('click', () => {
            document.getElementById('vendorTraceTreeWrapper').scrollLeft -= ((d3.select('#vendor-trace-svg').node().getBoundingClientRect().width + 150) - d3.select('#vendorTraceTreeWrapper').node().getBoundingClientRect().width);
            setZoomSide('next');
        });

        d3.select('#next-layers-button').on('click', () => {
            document.getElementById('vendorTraceTreeWrapper').scrollLeft += ((d3.select('#vendor-trace-svg').node().getBoundingClientRect().width + 150) - d3.select('#vendorTraceTreeWrapper').node().getBoundingClientRect().width);
            setZoomSide('prev');
        });
    };

    const onChangeDisclosedFilter = (list) => {
        setDisclosedFilter(list);
    };

    return (
        <div
            className="chart"
            style={{
                height: chartHeight,
                width: chartWidth,
                position: 'relative',
            }}
        >
            <VendorTracePrevalenceFilter selectedVendorName={selectedVendorName} />
            <VendorTracePriorToConsentFilter consentTypeName={consentTypeName} />
            {selectedMetric === 'is_disclosed' && selectedMetric ?
                <VendorTraceNonDisclosedFilter
                    defaultCheckedList={disclosedFilter}
                    onChangeDisclosed={(e) => onChangeDisclosedFilter(e)}
                /> : null}
            {selectedMetric !== 'is_disclosed' && selectedMetric ? <VendorTraceMetricLegend selectedMetric={selectedMetric} /> : null}
            <svg width={'100%'} height={'100%'} ref={ref} id={'vendor-trace-svg'} />
            <VendorTraceZoom scaleValue={scaleValue} selectedVendorName={selectedVendorName} />
        </div>
    );
}

const mapStateToProps = function (store) {
    return {
        selectedVendorTracePrevalenceFilter: store.diagnoseVendorTraceState.getIn(['vendorTracePrevalenceFilter', 'value']),
        searchByVendorNameMode: store.diagnoseVendorTraceState.getIn(['searchByVendorNameMode', 'value']),
        priorToConsentFilterList: store.diagnoseVendorTraceState.getIn(['priorToConsentFilterList', 'value']),
    };
};

const mapDispatchToProps = (dispatch) => {
    return bindActionCreators({
        setSelectedVendorTracePrevalenceFilters,
    }, dispatch);
};

export default connect(mapStateToProps, mapDispatchToProps)(Tree);
